// Professional content for Isha Rastogi website

export const professionalData = {
  // Professional Title - CRITICAL: Use this exact title everywhere
  title: "Child Psychologist & Neuro-Development Specialist",
  
  // Bio / Manifesto
  bio: {
    short: "Tri-continental academic training (USA, UK, Australia) in neurodevelopmental diagnostics. Founding Director of Modern Minds.",
    manifesto: "Behind every diagnostic label is a child with a unique story and a family looking for a bridge to understanding. As an internationally trained Child Psychologist & Clinical Neuro-Development Specialist, I bridge international clinical standards with deeply personalized care.",
    linkedin: "In high-stakes moments, families need more than reassurance—they need precision, accountability, and systems that work. I provide clarity through data-driven insights and structured behavioral frameworks that translate into tangible, sustainable outcomes."
  },

  // Education - Global Pedigree
  education: [
    {
      degree: "Master of Brain and Mind Sciences (MBMS)",
      institution: "University of Sydney",
      location: "Australia",
      year: "2025",
      description: "Advanced translational neuroscience and clinical research"
    },
    {
      degree: "Master of Science in Psychology (M.Sc.)",
      institution: "Nottingham Trent University",
      location: "United Kingdom",
      year: "2022",
      description: "Distinction in Statistics, behavioral data analysis"
    },
    {
      degree: "Bachelor of Science in Psychology & Public Health",
      institution: "Bowling Green State University",
      location: "United States",
      year: "2019",
      description: "Dual major in psychology and public health systems"
    }
  ],

  // Core Pillars of Practice
  pillars: [
    {
      title: "Psycho-Diagnostics",
      description: "Precision mapping of neuro-developmental disorders including ASD and ADHD. I specialize in gold-standard instrumentation (ADOS-2, WISC-IV, ISAA) to bypass parent-reporting bias and find the objective baseline."
    },
    {
      title: "Behavioral Engineering",
      description: "Using ABA-integrated protocols and Positive Behaviour Support (PBS) to build functional, sustainable life skills."
    },
    {
      title: "Systemic Restructuring",
      description: "Mapping intergenerational family patterns to modify parenting styles and mitigate the psychosocial stressors that impact a child's development."
    }
  ],

  // Professional Credentials - Categorized with Certificates
  credentials: {
    diagnostic: [
      {
        title: "ADOS-2 Clinically Reliable Assessor (Modules 1-4)",
        detail: "Gold standard autism diagnostic tool, certified for all modules",
        certificate: null // Certificate not provided
      },
      {
        title: "Pearson WISC-IV Certification",
        detail: "Wechsler Intelligence Scale for Children - cognitive assessment",
        certificate: null // Certificate not provided
      },
      {
        title: "ISAA (Indian Scale for Assessment of Autism)",
        detail: "Culturally-adapted autism assessment tool",
        certificate: null // Certificate not provided
      },
      {
        title: "Bender Gestalt Test (BGT)",
        detail: "Visual-motor integration and neuropsychological assessment",
        certificate: null // Certificate not provided
      },
      {
        title: "Rorschach Exner System Certification",
        detail: "Projective personality assessment",
        certificate: null // Certificate not provided
      },
      {
        title: "NIMHANS Battery",
        detail: "Comprehensive neuropsychological assessment battery",
        certificate: null // Certificate not provided
      },
      {
        title: "Dyslexia Screening Tests (DST)",
        detail: "Prasad Psycho Pvt. Ltd. (2026)",
        certificate: "/certificates/dst-dyslexia-cert.pdf"
      },
      {
        title: "Learning Disability Profiling",
        detail: "Intensive Training Programme, SensAble Child & St. Teresa's College (2026)",
        certificate: "/certificates/sensable-child-cert.pdf"
      },
      {
        title: "Psychometric Assessment for Professionals",
        detail: "Counsellors Council of India (2026)",
        certificate: "/certificates/psychometric-assessment-cert.pdf"
      }
    ],
    behavioral: [
      {
        title: "Registered Behaviour Technician (RBT®)",
        detail: "BACB certified behavior therapy specialist, Autism Partnership Foundation (May 2025)",
        certificate: "/certificates/rbt-cert.pdf"
      },
      {
        title: "Applied Behavior Analysis (ABA)",
        detail: "Evidence-based behavioral intervention protocols",
        certificate: null // Certificate not provided
      },
      {
        title: "Positive Behaviour Support (PBS)",
        detail: "Systematic approach to behavior modification",
        certificate: null // Certificate not provided
      },
      {
        title: "Cognitive Behavioral Therapy (CBT)",
        detail: "Advanced CBT Masterclass certification",
        certificate: null // Certificate not provided
      },
      {
        title: "Dialectical Behavior Therapy (DBT)",
        detail: "Emotion regulation and distress tolerance strategies",
        certificate: null // Certificate not provided
      }
    ],
    traumaNeurodiversity: [
      {
        title: "Certified Trauma-Informed Coach",
        detail: "Specialized trauma and stress management protocols, The Centre for Healing (Oct 2024)",
        certificate: null // Certificate not provided
      },
      {
        title: "Certified Autism-Informed Professional (ASDI)",
        detail: "Advanced autism spectrum disorder intervention, Evergreen Certifications (Sep 2025 – Sep 2026)",
        certificate: null // Certificate not provided
      },
      {
        title: "PESI Neurodiversity Specialist Training",
        detail: "Neurodiversity-affirming clinical practices, PESI AU (Jul 2025)",
        certificate: "/certificates/pesi-neurodivergent-cert.pdf"
      },
      {
        title: "Polyvagal Theory & Neuroscience of Safety",
        detail: "Advanced nervous system regulation certification, PDP Seminars (2025)",
        certificate: null // Certificate not provided
      },
      {
        title: "Sensory Processing and Integration",
        detail: "Specialized sensory-motor intervention training, Sensory Integration Education (2025)",
        certificate: null // Certificate not provided
      },
      {
        title: "GMBPsS (UK)",
        detail: "Graduate Member of the British Psychological Society",
        certificate: null // Certificate not provided
      }
    ]
  },

  // Professional Affiliations
  affiliations: [
    {
      name: "American Psychological Association (APA)",
      role: "International Affiliate",
      logo: "APA"
    },
    {
      name: "British Psychological Society (BPS)",
      role: "Graduate Member (GMBPsS)",
      logo: "BPS"
    },
    {
      name: "Australian Psychological Society (APS)",
      role: "Affiliate Member",
      logo: "APS"
    },
    {
      name: "International Association of Applied Psychology (IAAP)",
      role: "Member",
      logo: "IAAP"
    }
  ],

  // Research & Publications
  research: {
    thesis: {
      title: "Differential Analysis of Micro-Emotional Expressions in Autistic and Neurotypical Children using OpenFace 2.0 and the Facial Action Coding System (FACS) in Naturalistic Interactions",
      journal: "Journal of Psychology and Psychiatry",
      year: "2026",
      abstract: "Spearheaded specialized translational research utilizing the Facial Action Coding System (FACS) and OpenFace 2.0 to conduct differential analyses of micro-emotional expressions and neurological responses in autistic versus neurotypical pediatric populations during naturalistic interactions.",
      methodology: "OpenFace 2.0, FACS, R Programming, Computational Psychology"
    },
    publications: [
      {
        title: "Exploration of Symptoms of OCD and Anxiety in Association with Fear of COVID-19",
        journal: "Indian Journal of Public Health Research & Development",
        year: "2026",
        description: "Large-scale behavioral data analysis exploring the acute psychopathological intersections of OCD, anxiety, and distress tolerance architectures."
      }
    ],
    orcid: "0000-0003-4065-0000"
  },

  // Technical Skills
  technicalSkills: [
    "Facial Action Coding System (FACS)",
    "OpenFace 2.0",
    "R Programming",
    "SAS",
    "SPSS",
    "Qualtrics",
    "Psychophysiological Monitoring",
    "Computational Psychology"
  ],

  // Modern Minds Clinic
  clinic: {
    name: "Modern Minds",
    subtitle: "Center for Child Psychology, Behavioural Health & Parenting Consultancy",
    website: "https://modernminds.co.in",
    role: "Founder & Clinical Director",
    description: "A sovereign clinical institute dedicated to the intersection of psychometric precision and functional behavioral engineering, operating on a tiered 'Closed-Loop Intervention' model.",
    location: "Gomti Nagar, Lucknow, India",
    services: [
      "High-resolution psychometric mapping (ADOS-2, WISC-IV/V, Vineland-3)",
      "ABA/PBS behavioral frameworks",
      "Intergenerational trauma resolution",
      "Neuro-affirming home environment design"
    ]
  },

  // Contact Information
  contact: {
    email: "isha@modernminds.co.in",
    phone: "+91 9569170611",
    whatsapp: "+91 9569170611",
    address: "1/4A, Vineet Khand, Gomti Nagar, Lucknow, UP 226010, India",
    location: "1/4A, Vineet Khand, Gomti Nagar, Lucknow, UP 226010, India",
    coordinates: {
      lat: 26.8467,
      lng: 80.9462
    }
  },

  // Social Media
  social: {
    instagram: "https://www.instagram.com/isharastogipsychology/",
    instagramQR: "https://customer-assets.emergentagent.com/job_distracted-zhukovsky-2/artifacts/l8swghyw_image.png",
    linkedin: null // Add if available
  },

  // Parvarish AI
  parvarishAI: {
    title: "Parvarish AI",
    subtitle: "परवरिश • Family Wellness",
    tagline: "Your Compassionate Guide for Family Wellness",
    description: "An AI-powered mental health companion designed specifically for Indian parents and families. Get culturally-sensitive guidance on parenting, child behavior, academic stress, and your own mental wellbeing.",
    link: "https://parvarish.cura.bio",
    features: [
      "AI-Powered Guidance: Understanding Indian family dynamics, academic pressure, and cultural nuances",
      "12+ Indian Languages: Support in Hindi, Tamil, Telugu, Bengali, Marathi, Gujarati, and more",
      "Crisis Support: Immediate access to Indian mental health helplines (iCall, NIMHANS, Vandrevala)",
      "Topics: Board exam stress, child behavior, parenting burnout, joint family dynamics, screen time, and more"
    ],
    developmentNote: "This application is currently under active development and extensive testing. Parvarish AI is NOT a replacement for professional mental health care, therapy, or medical advice.",
    badge: "Under Development"
  }
};
